import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, Package, ShoppingCart, UploadCloud, LineChart, MessageSquare, Users, Droplet, Save, Check, Palette, Award, Trophy, Star, Bell } from "lucide-react";
import { api, formatApiErrorDetail, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  name: "", category: "", price: "", description: "", length: "", pack_size: "",
  colors: "", images: "", featured: false, in_stock: true, stock: "",
  custom: false, mica_options: "", sheen_options: "", pearl_options: "", glitter_options: "",
};

const adminLayerHas = (l) => !!(l && (l.dyes?.length || l.micas?.length || l.pearls?.length || l.glitters?.length));
const adminLayerLine = (l) =>
  [
    l?.dyes?.length ? `Dyes: ${l.dyes.join(", ")}` : "",
    l?.micas?.length ? `Mica: ${l.micas.join(", ")}` : "",
    l?.pearls?.length ? `Pearl: ${l.pearls.join(", ")}` : "",
    l?.glitters?.length ? `Glitter: ${l.glitters.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

const BLEND_GROUPS = [
  { key: "dyes", label: "Dyes", group: "pigment", base: true },
  { key: "micas", label: "Mica", group: "mica", base: true },
  { key: "pearls", label: "Pearl", group: "pearl", base: false },
  { key: "glitters", label: "Glitter", group: "glitter", base: false },
];
const emptyBlendLayer = () => ({ dyes: [], micas: [], pearls: [], glitters: [] });

// Multi-select picker for the admin Starter Blend builder (reads from the options Library)
const AdminLayerPicker = ({ library, layer, onToggle, idPrefix }) => {
  const [q, setQ] = useState({});
  return (
    <div className="space-y-4">
      {BLEND_GROUPS.map((g) => {
        const opts = library[g.group] || [];
        if (!opts.length) return null;
        const selected = layer[g.key] || [];
        const term = (q[g.key] || "").toLowerCase().trim();
        const shown = term ? opts.filter((o) => o.name.toLowerCase().includes(term)) : opts;
        return (
          <div key={g.key} data-testid={`${idPrefix}-${g.key}`}>
            <span className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-slate-500">
              {g.label}{g.base && <span className="text-orange-500/80"> · base</span>}
              {selected.length > 0 && <span className="text-orange-400/90"> · {selected.length}</span>}
            </span>
            {opts.length > 16 && (
              <input
                type="text"
                value={q[g.key] || ""}
                onChange={(e) => setQ((s) => ({ ...s, [g.key]: e.target.value }))}
                placeholder={`Search ${g.label.toLowerCase()}…`}
                className="w-full bg-transparent border border-white/15 mt-2 px-2 py-1.5 focus:border-orange-500 focus:outline-none text-xs text-slate-200"
              />
            )}
            <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto">
              {shown.map((o) => {
                const isSel = selected.includes(o.name);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => onToggle(g.key, o.name)}
                    className={`flex items-center gap-1.5 font-mono-plex text-[11px] tracking-wide px-2.5 py-1.5 border transition-colors ${
                      isSel ? "bg-orange-500/15 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                    }`}
                    data-testid={`${idPrefix}-${g.key}-${o.name}`}
                  >
                    {o.hex && <span className="inline-block w-3 h-3 rounded-full border border-white/25" style={{ backgroundColor: o.hex }} />}
                    {o.name}
                    {isSel && <Check className="w-3 h-3 text-orange-400" />}
                  </button>
                );
              })}
              {shown.length === 0 && <span className="font-mono-plex text-[11px] text-slate-600">No matches</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [tForm, setTForm] = useState({ name: "", location: "", quote: "", image: "" });
  const [tUploading, setTUploading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [colorForm, setColorForm] = useState({ color_name: "", color_description: "", color_image: "" });
  const [colorUploading, setColorUploading] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [library, setLibrary] = useState({ color: [], pigment: [], mica: [], sheen: [], pearl: [], glitter: [] });
  const [newOpt, setNewOpt] = useState({ color: "", pigment: "", mica: "", sheen: "", pearl: "", glitter: "" });
  const [bulkOpt, setBulkOpt] = useState({ color: "", pigment: "", mica: "", sheen: "", pearl: "", glitter: "" });
  const [recipes, setRecipes] = useState([]);
  const [blendTypes, setBlendTypes] = useState({});
  const [showBlendForm, setShowBlendForm] = useState(false);
  const [savingBlend, setSavingBlend] = useState(false);
  const [blend, setBlend] = useState({ name: "", product_type: "", split: false });
  const [blendSingle, setBlendSingle] = useState(emptyBlendLayer());
  const [blendTop, setBlendTop] = useState(emptyBlendLayer());
  const [blendBottom, setBlendBottom] = useState(emptyBlendLayer());
  const [showNewCat, setShowNewCat] = useState(false);
  const [ambassadors, setAmbassadors] = useState([]);
  const [restockAlerts, setRestockAlerts] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [newAmb, setNewAmb] = useState({ name: "", email: "" });
  const [monthly, setMonthly] = useState({ month: "", rows: [] });
  const [bonusPts, setBonusPts] = useState(5);
  const [ambSettings, setAmbSettings] = useState({ ambassador_points_per_pack: 10, ambassador_discount_pct: 15 });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        urls.push(`${BACKEND_URL}${data.url}`);
      }
      setForm((prev) => {
        const existing = prev.images.split(",").map((s) => s.trim()).filter(Boolean);
        return { ...prev, images: [...existing, ...urls].join(", ") };
      });
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.split(",").map((s) => s.trim()).filter(Boolean).filter((u) => u !== url).join(", "),
    }));
  };

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  const loadProducts = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const loadOrders = useCallback(() => {
    api.get("/orders", { params: { include_archived: showArchived } }).then((r) => setOrders(r.data)).catch(() => {});
  }, [showArchived]);
  const loadTestimonials = useCallback(() => {
    api.get("/testimonials/all").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);
  const loadSubscribers = useCallback(() => {
    api.get("/club/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);
  const loadSettings = useCallback(() => {
    api.get("/settings").then((r) => {
      setColorForm({
        color_name: r.data.color_name || "",
        color_description: r.data.color_description || "",
        color_image: r.data.color_image || "",
      });
      setAmbSettings({
        ambassador_points_per_pack: r.data.ambassador_points_per_pack ?? 10,
        ambassador_discount_pct: r.data.ambassador_discount_pct ?? 15,
      });
    }).catch(() => {});
  }, []);
  const loadLibrary = useCallback(() => {
    api.get("/options").then((r) => {
      const grouped = { color: [], pigment: [], mica: [], sheen: [], pearl: [], glitter: [] };
      r.data.forEach((o) => { if (grouped[o.group]) grouped[o.group].push(o); });
      setLibrary(grouped);
    }).catch(() => {});
  }, []);
  const loadRecipes = useCallback(() => {
    api.get("/admin/recipes").then((r) => setRecipes(r.data)).catch(() => {});
    api.get("/custom-types").then((r) => setBlendTypes(r.data)).catch(() => {});
  }, []);
  const loadAmbassadors = useCallback(() => {
    api.get("/admin/ambassadors").then((r) => setAmbassadors(r.data)).catch(() => {});
  }, []);
  const loadRestockAlerts = useCallback(() => {
    api.get("/restock-alerts").then((r) => setRestockAlerts(r.data)).catch(() => {});
  }, []);
  const loadRedemptions = useCallback(() => {
    api.get("/admin/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
  }, []);
  const loadMonthly = useCallback(() => {
    api.get("/admin/ambassador-monthly").then((r) => setMonthly(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      loadProducts();
      loadOrders();
      loadTestimonials();
      loadSubscribers();
      loadSettings();
      loadLibrary();
      loadRecipes();
      loadAmbassadors();
      loadRestockAlerts();
      loadRedemptions();
      loadMonthly();
    }
  }, [user, loadProducts, loadOrders, loadTestimonials, loadSubscribers, loadSettings, loadLibrary, loadRecipes, loadAmbassadors, loadRestockAlerts, loadRedemptions, loadMonthly]);

  if (loading || !user) return <div className="bg-[#04070A] min-h-screen" />;

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowNewCat(false);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setShowNewCat(false);
    setForm({
      name: p.name, category: p.category, price: String(p.price), description: p.description,
      length: p.length, pack_size: p.pack_size, colors: (p.colors || []).join(", "),
      images: (p.images || []).join(", "), featured: p.featured, in_stock: p.in_stock,
      stock: String(p.stock ?? ""),
      custom: p.custom || false,
      mica_options: (p.mica_options || []).join(", "),
      sheen_options: (p.sheen_options || []).join(", "),
      pearl_options: (p.pearl_options || []).join(", "),
      glitter_options: (p.glitter_options || []).join(", "),
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if ((form.category || "").trim().toLowerCase() === "all") {
      toast.error("“All” is reserved for the shop filter — pick or add a real category");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      price: parseFloat(form.price) || 0,
      description: form.description,
      length: form.length,
      pack_size: form.pack_size,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
      featured: form.featured,
      in_stock: form.in_stock,
      stock: parseInt(form.stock) || 0,
      custom: form.custom,
      mica_options: form.mica_options.split(",").map((s) => s.trim()).filter(Boolean),
      sheen_options: form.sheen_options.split(",").map((s) => s.trim()).filter(Boolean),
      pearl_options: form.pearl_options.split(",").map((s) => s.trim()).filter(Boolean),
      glitter_options: form.glitter_options.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setShowForm(false);
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      loadProducts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch {
      toast.error("Update failed");
    }
  };

  const archiveOrder = async (id, archived) => {
    try {
      await api.patch(`/orders/${id}/archive`, { archived });
      toast.success(archived ? "Order archived" : "Order restored");
      loadOrders();
    } catch {
      toast.error("Action failed");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Permanently delete this order? If it was paid, its revenue is also removed from your Profit Tracker.")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      loadOrders();
    } catch {
      toast.error("Delete failed");
    }
  };

  const archiveShipped = async () => {
    try {
      const { data } = await api.post("/orders/archive-shipped");
      toast.success(`Archived ${data.archived} shipped order${data.archived === 1 ? "" : "s"}`);
      loadOrders();
    } catch {
      toast.error("Action failed");
    }
  };

  const uploadTestimonialImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setTForm((prev) => ({ ...prev, image: `${BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setTUploading(false);
      e.target.value = "";
    }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    if (!tForm.quote.trim() && !tForm.image) {
      toast.error("Add a quote or a photo");
      return;
    }
    try {
      await api.post("/testimonials", tForm);
      toast.success("Testimonial added");
      setTForm({ name: "", location: "", quote: "", image: "" });
      loadTestimonials();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const delTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Deleted");
      loadTestimonials();
    } catch {
      toast.error("Delete failed");
    }
  };

  const approveTestimonial = async (id) => {
    try {
      await api.patch(`/testimonials/${id}/approve`);
      toast.success("Approved — now live");
      loadTestimonials();
    } catch {
      toast.error("Approve failed");
    }
  };

  const uploadColorImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setColorUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setColorForm((prev) => ({ ...prev, color_image: `${BACKEND_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setColorUploading(false);
      e.target.value = "";
    }
  };

  const saveColor = async (e) => {
    e.preventDefault();
    setSavingColor(true);
    try {
      await api.put("/settings", colorForm);
      toast.success("Color of the Month saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingColor(false);
    }
  };

  const addOption = async (group) => {
    const name = (newOpt[group] || "").trim();
    if (!name) return;
    try {
      await api.post("/options", { group, name });
      setNewOpt((s) => ({ ...s, [group]: "" }));
      loadLibrary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add");
    }
  };

  const bulkAddOptions = async (group) => {
    const names = (bulkOpt[group] || "").trim();
    if (!names) return;
    try {
      const { data } = await api.post("/options/bulk", { group, names });
      toast.success(`Added ${data.added} (${data.submitted - data.added} already existed)`);
      setBulkOpt((s) => ({ ...s, [group]: "" }));
      loadLibrary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk add failed");
    }
  };

  const toggleOption = async (id, in_stock) => {
    try {
      await api.patch(`/options/${id}`, { in_stock });
      loadLibrary();
    } catch {
      toast.error("Update failed");
    }
  };

  const setOptionHex = async (id, hex) => {
    // optimistic: update local state immediately so the swatch reflects instantly
    setLibrary((lib) => {
      const next = { ...lib };
      for (const g of Object.keys(next)) {
        next[g] = next[g].map((o) => (o.id === id ? { ...o, hex } : o));
      }
      return next;
    });
    try {
      await api.patch(`/options/${id}`, { hex });
    } catch {
      toast.error("Swatch update failed");
      loadLibrary();
    }
  };

  const deleteOption = async (id) => {
    try {
      await api.delete(`/options/${id}`);
      loadLibrary();
    } catch {
      toast.error("Delete failed");
    }
  };

  const promoteRecipe = async (rid) => {
    try {
      const { data } = await api.post(`/admin/recipes/${rid}/promote`);
      toast.success(data.created ? `Added "${data.name}" to Drift Colors` : `"${data.name}" is already a Drift Color`);
      loadRecipes();
      loadLibrary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save as color");
    }
  };

  const toggleFeatureRecipe = async (rid) => {
    try {
      const { data } = await api.post(`/admin/recipes/${rid}/feature`);
      setRecipes((prev) => prev.map((r) => (r.id === rid ? { ...r, featured: data.featured } : r)));
      toast.success(data.featured ? "Added to Starter Blends" : "Removed from Starter Blends");
    } catch {
      toast.error("Could not update Starter Blend");
    }
  };

  const toggleBlendItem = (setter) => (key, name) =>
    setter((s) => ({ ...s, [key]: s[key].includes(name) ? s[key].filter((n) => n !== name) : [...s[key], name] }));
  const resetBlendForm = () => {
    setBlend({ name: "", product_type: "", split: false });
    setBlendSingle(emptyBlendLayer());
    setBlendTop(emptyBlendLayer());
    setBlendBottom(emptyBlendLayer());
    setShowBlendForm(false);
  };
  const createStarterBlend = async () => {
    const base = (l) => l.dyes.length || l.micas.length;
    if (!blend.name.trim()) { toast.error("Give the blend a name"); return; }
    if (blend.split ? (!base(blendTop) || !base(blendBottom)) : !base(blendSingle)) {
      toast.error("Select at least one Dye or Mica as the base"); return;
    }
    setSavingBlend(true);
    try {
      await api.post("/admin/starter-blends", {
        name: blend.name.trim(),
        product_type: blend.product_type || "",
        split: blend.split,
        base: blend.split ? emptyBlendLayer() : blendSingle,
        top: blend.split ? blendTop : emptyBlendLayer(),
        bottom: blend.split ? blendBottom : emptyBlendLayer(),
      });
      toast.success(`"${blend.name.trim()}" added to Starter Blends`);
      resetBlendForm();
      loadRecipes();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create blend");
    } finally {
      setSavingBlend(false);
    }
  };
  const deleteRecipe = async (rid) => {
    if (!window.confirm("Delete this recipe/blend permanently?")) return;
    try {
      await api.delete(`/admin/recipes/${rid}`);
      setRecipes((prev) => prev.filter((r) => r.id !== rid));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const createAmbassador = async (e) => {
    e.preventDefault();
    if (!newAmb.name.trim() || !newAmb.email.trim()) return;
    try {
      await api.post("/admin/ambassadors", { name: newAmb.name.trim(), email: newAmb.email.trim() });
      setNewAmb({ name: "", email: "" });
      toast.success("Ambassador created");
      loadAmbassadors();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    }
  };

  const toggleAmbassador = async (id, active) => {
    setAmbassadors((prev) => prev.map((a) => (a.id === id ? { ...a, active } : a))); // optimistic
    try {
      await api.patch(`/admin/ambassadors/${id}`, { active });
      loadAmbassadors();
    } catch {
      toast.error("Update failed");
      loadAmbassadors();
    }
  };

  const deleteAmbassador = async (id) => {
    try {
      await api.delete(`/admin/ambassadors/${id}`);
      loadAmbassadors();
      loadRedemptions();
    } catch {
      toast.error("Delete failed");
    }
  };

  const fulfillRedemption = async (id) => {
    try {
      await api.patch(`/admin/redemptions/${id}`, { status: "fulfilled" });
      loadRedemptions();
      toast.success("Marked fulfilled");
    } catch {
      toast.error("Update failed");
    }
  };

  const awardWinner = async (id, type) => {
    try {
      await api.post(`/admin/ambassadors/${id}/award`, type === "pack" ? { type: "pack" } : { type: "points", points: parseInt(bonusPts) || 5 });
      toast.success(type === "pack" ? "Free pack awarded" : `${parseInt(bonusPts) || 5} bonus points awarded`);
      loadAmbassadors();
      loadRedemptions();
    } catch {
      toast.error("Could not award");
    }
  };

  const saveAmbSettings = async () => {
    try {
      await api.put("/settings", {
        ambassador_points_per_pack: parseInt(ambSettings.ambassador_points_per_pack) || 10,
        ambassador_discount_pct: parseInt(ambSettings.ambassador_discount_pct) || 15,
      });
      toast.success("Ambassador settings saved");
      loadAmbassadors();
    } catch {
      toast.error("Could not save settings");
    }
  };


  const toggleFormColor = (name) => {
    const current = form.colors.split(",").map((s) => s.trim()).filter(Boolean);
    const next = current.includes(name) ? current.filter((c) => c !== name) : [...current, name];
    setForm({ ...form, colors: next.join(", ") });
  };

  return (
    <div className="bg-[#04070A] min-h-screen text-slate-200">
      <div className="noise-overlay" />
      <header className="border-b border-white/5 px-6 md:px-10 h-16 flex items-center justify-between sticky top-0 bg-[#04070A]/80 backdrop-blur-xl z-50">
        <Link to="/" className="font-display font-semibold text-base uppercase">
          Drift Tackle<span className="text-orange-500">.</span> <span className="text-slate-500 text-xs font-mono-plex tracking-widest ml-2">ADMIN</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/admin/ledger" className="flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase text-slate-400 hover:text-orange-500 transition-colors" data-testid="ledger-link">
            <LineChart className="w-4 h-4" /> Profit Tracker
          </Link>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase text-slate-400 hover:text-orange-500 transition-colors" data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center gap-2 mb-10">
          <button onClick={() => setTab("products")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "products" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-products">
            <Package className="w-4 h-4" /> Products ({products.length})
          </button>
          <button onClick={() => setTab("orders")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "orders" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-orders">
            <ShoppingCart className="w-4 h-4" /> Orders ({orders.length})
          </button>
          <button onClick={() => setTab("alerts")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "alerts" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-alerts">
            <Bell className="w-4 h-4" /> Alerts ({restockAlerts.reduce((s, a) => s + a.count, 0)})
          </button>
          <button onClick={() => setTab("testimonials")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "testimonials" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-testimonials">
            <MessageSquare className="w-4 h-4" /> Testimonials ({testimonials.length})
          </button>
          <button onClick={() => setTab("club")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "club" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-club">
            <Users className="w-4 h-4" /> Club ({subscribers.length})
          </button>
          <button onClick={() => setTab("library")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "library" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-library">
            <Palette className="w-4 h-4" /> Library
          </button>
          <button onClick={() => setTab("recipes")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "recipes" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-recipes">
            <Droplet className="w-4 h-4" /> Recipes ({recipes.length})
          </button>
          <button onClick={() => setTab("ambassadors")} className={`flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase px-5 py-3 border transition-colors ${tab === "ambassadors" ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400"}`} data-testid="tab-ambassadors">
            <Award className="w-4 h-4" /> Ambassadors ({ambassadors.length})
          </button>
        </div>

        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h1 className="font-display text-3xl uppercase tracking-tight">Products</h1>
              <button onClick={openNew} className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-widest uppercase px-5 py-3 hover:bg-orange-600 transition-colors" data-testid="add-product-button">
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>
            <div className="border border-white/5 divide-y divide-white/5" data-testid="admin-product-list">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02]" data-testid={`admin-product-${p.id}`}>
                  <div className="w-14 h-14 bg-[#0A1118] overflow-hidden shrink-0">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase tracking-tight truncate">{p.name}</p>
                    <p className="font-mono-plex text-xs text-slate-500">{p.category} · ${p.price?.toFixed(2)} {p.featured && "· ★ Featured"} {!p.in_stock && "· Sold out"}</p>
                  </div>
                  <button onClick={() => openEdit(p)} className="p-2 text-slate-400 hover:text-orange-500 transition-colors" data-testid={`edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="p-2 text-slate-400 hover:text-red-400 transition-colors" data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h1 className="font-display text-3xl uppercase tracking-tight">Orders</h1>
              <div className="flex items-center gap-3">
                <button onClick={archiveShipped} className="font-mono-plex text-[11px] tracking-widest uppercase border border-white/15 text-slate-300 px-4 py-2 hover:bg-white/5 transition-colors" data-testid="clear-shipped-btn">
                  Clear Shipped
                </button>
                <label className="flex items-center gap-2 cursor-pointer font-mono-plex text-[11px] tracking-widest uppercase text-slate-400" data-testid="show-archived-toggle">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-orange-500 w-4 h-4" />
                  Show archived
                </label>
              </div>
            </div>
            {orders.length === 0 ? (
              <p className="text-slate-500 font-mono-plex text-sm">{showArchived ? "No orders." : "No active orders — nice and tidy."}</p>
            ) : (
              <div className="flex flex-col gap-4" data-testid="admin-order-list">
                {orders.map((o) => (
                  <div key={o.id} className={`border p-5 ${o.archived ? "border-white/5 opacity-60" : "border-white/5"}`} data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap justify-between gap-4">
                      <div>
                        <p className="font-display uppercase tracking-tight flex items-center gap-2">
                          {o.customer_name}
                          {o.archived && <span className="font-mono-plex text-[9px] tracking-widest uppercase bg-slate-500/20 text-slate-400 px-2 py-0.5">Archived</span>}
                        </p>
                        <p className="font-mono-plex text-xs text-slate-500">{o.email} · {o.phone || "no phone"}</p>
                        <p className="font-mono-plex text-xs text-slate-500 mt-1">{o.address}</p>
                        {o.note && <p className="text-slate-400 text-sm mt-2 italic">&ldquo;{o.note}&rdquo;</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-display text-xl text-orange-500">${o.total?.toFixed(2)}</p>
                        <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="mt-2 bg-[#0A1118] border border-white/15 text-slate-300 font-mono-plex text-xs px-3 py-1.5 focus:outline-none focus:border-orange-500" data-testid={`order-status-${o.id}`}>
                          <option value="new">New</option>
                          <option value="invoiced">Invoiced</option>
                          <option value="paid">Paid</option>
                          <option value="shipped">Shipped</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4 border-t border-white/5 pt-3 flex flex-col gap-1">
                      {o.items?.map((it, i) => (
                        <div key={i} className="flex justify-between font-mono-plex text-xs text-slate-400">
                          <span>
                            {it.quantity}× {it.name} {it.color ? `(${it.color})` : ""}
                            {it.custom_specs && (
                              <span className="block text-[10px] text-orange-400/70 mt-0.5">
                                {Object.entries(it.custom_specs).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
                              </span>
                            )}
                          </span>
                          <span>${(it.price * it.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-3">
                      <button onClick={() => archiveOrder(o.id, !o.archived)} className="font-mono-plex text-[10px] tracking-widest uppercase border border-white/15 text-slate-200 px-3 py-1.5 hover:border-orange-500 hover:text-orange-400 transition-colors" data-testid={`order-archive-${o.id}`}>
                        {o.archived ? "Restore" : "Archive"}
                      </button>
                      <button onClick={() => deleteOrder(o.id)} className="flex items-center gap-1.5 font-mono-plex text-[10px] tracking-widest uppercase border border-white/15 text-slate-300 px-3 py-1.5 hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`order-delete-${o.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "alerts" && (
          <div data-testid="admin-alerts">
            <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Restock Alerts</h1>
            <p className="text-slate-400 text-sm mb-8 max-w-2xl">
              Shoppers waiting on sold-out baits, grouped by product — highest demand first. When you flip a product back in stock (Products tab), everyone waiting gets emailed automatically and their alert clears.
            </p>
            {restockAlerts.length === 0 ? (
              <p className="text-slate-500 font-mono-plex text-sm tracking-widest uppercase" data-testid="alerts-empty">No one is waiting on a restock right now.</p>
            ) : (
              <div className="space-y-4" data-testid="alerts-list">
                {restockAlerts.map((a) => {
                  const prod = products.find((p) => p.id === a.product_id);
                  const backInStock = prod && prod.in_stock && prod.stock > 0;
                  return (
                    <div key={a.product_id} className="bg-[#0A1118] border border-white/5 p-5" data-testid={`alert-product-${a.product_id}`}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="bg-orange-500/15 border border-orange-500/40 text-orange-400 font-mono-plex text-xs px-2.5 py-1 shrink-0" data-testid={`alert-count-${a.product_id}`}>
                            {a.count} waiting
                          </span>
                          <p className="font-display uppercase tracking-tight truncate">{a.product_name}</p>
                        </div>
                        {backInStock && (
                          <span className="font-mono-plex text-[10px] tracking-widest uppercase text-green-400">Back in stock — alerts sent</span>
                        )}
                      </div>
                      <div className="mt-3 divide-y divide-white/5">
                        {a.emails.map((e) => (
                          <div key={e.email} className="flex items-center justify-between py-2" data-testid={`alert-email-${a.product_id}-${e.email}`}>
                            <span className="font-mono-plex text-xs text-slate-300">{e.email}</span>
                            <span className="font-mono-plex text-[10px] text-slate-500">
                              {e.since ? `since ${new Date(e.since).toLocaleDateString()}` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}


        {tab === "testimonials" && (
          <div data-testid="admin-testimonials">
            <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Testimonials</h1>
            <p className="text-slate-400 text-sm mb-8">Add a customer photo, a quote, or both. They appear on the public Testimonials page.</p>
            <form onSubmit={addTestimonial} className="bg-[#0A1118] border border-white/5 p-6 grid sm:grid-cols-2 gap-5 mb-10">
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Customer Name</label>
                <input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="t-name" />
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Location / Water (optional)</label>
                <input value={tForm.location} onChange={(e) => setTForm({ ...tForm, location: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="t-location" />
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Quote (optional)</label>
                <textarea rows={2} value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none resize-none" data-testid="t-quote" />
              </div>
              <div className="sm:col-span-2 flex items-center gap-4">
                {tForm.image ? (
                  <div className="relative w-24 h-24 border border-white/10 overflow-hidden">
                    <img src={tForm.image} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setTForm({ ...tForm, image: "" })} className="absolute top-0 right-0 bg-black/70 p-1"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ) : (
                  <label className="w-24 h-24 border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-orange-500 transition-colors" data-testid="t-upload-label">
                    <UploadCloud className="w-5 h-5 text-slate-400" />
                    <span className="font-mono-plex text-[9px] uppercase text-slate-500">{tUploading ? "…" : "Photo"}</span>
                    <input type="file" accept="image/*" onChange={uploadTestimonialImg} className="hidden" disabled={tUploading} data-testid="t-upload-input" />
                  </label>
                )}
                <button type="submit" className="ml-auto flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-widest uppercase px-5 py-3 hover:bg-orange-600 transition-colors" data-testid="t-add"><Plus className="w-4 h-4" /> Add Testimonial</button>
              </div>
            </form>
            {testimonials.length === 0 ? (
              <p className="text-slate-500 text-sm">No testimonials yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-testimonial-list">
                {testimonials.map((t) => (
                  <div key={t.id} className={`border bg-[#0A1118] overflow-hidden ${t.approved ? "border-white/5" : "border-orange-500/50"}`} data-testid={`admin-testimonial-${t.id}`}>
                    {t.image && <img src={fileUrl(t.image)} alt="" className="w-full h-40 object-cover" />}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {!t.approved && <span className="font-mono-plex text-[9px] tracking-widest uppercase bg-orange-500/15 text-orange-400 px-2 py-0.5" data-testid={`t-pending-${t.id}`}>Pending</span>}
                        {t.source === "member" && <span className="font-mono-plex text-[9px] tracking-widest uppercase text-slate-500">Member submission</span>}
                      </div>
                      {t.quote && <p className="text-slate-300 text-sm leading-relaxed mb-2">“{t.quote}”</p>}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-display uppercase text-sm">{t.name || "—"}</p>
                          {t.location && <p className="font-mono-plex text-[10px] text-slate-500">{t.location}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                          {!t.approved && (
                            <button onClick={() => approveTestimonial(t.id)} className="flex items-center gap-1 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-widest uppercase px-3 py-1.5 hover:bg-orange-600 transition-colors" data-testid={`t-approve-${t.id}`}><Check className="w-3 h-3" /> Approve</button>
                          )}
                          <button onClick={() => delTestimonial(t.id)} className="text-slate-500 hover:text-red-400 transition-colors" data-testid={`t-del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "club" && (
          <div data-testid="admin-club">
            <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Drift Club</h1>
            <p className="text-slate-400 text-sm mb-8">Set the Color of the Month (shown on the public Club page) and see your active subscribers.</p>

            <form onSubmit={saveColor} className="bg-[#0A1118] border border-white/5 p-6 grid sm:grid-cols-2 gap-5 mb-12">
              <div className="sm:col-span-2 flex items-center gap-2">
                <Droplet className="w-4 h-4 text-orange-500" />
                <span className="font-mono-plex text-[11px] tracking-[0.2em] uppercase text-orange-400">Color of the Month</span>
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Color Name</label>
                <input value={colorForm.color_name} onChange={(e) => setColorForm({ ...colorForm, color_name: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="color-name" placeholder="e.g. Bluegill Flash" />
              </div>
              <div className="flex items-end gap-4">
                {colorForm.color_image ? (
                  <div className="relative w-20 h-20 border border-white/10 overflow-hidden">
                    <img src={colorForm.color_image} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setColorForm({ ...colorForm, color_image: "" })} className="absolute top-0 right-0 bg-black/70 p-1"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ) : (
                  <label className="w-20 h-20 border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-orange-500 transition-colors" data-testid="color-upload-label">
                    <UploadCloud className="w-5 h-5 text-slate-400" />
                    <span className="font-mono-plex text-[9px] uppercase text-slate-500">{colorUploading ? "…" : "Photo"}</span>
                    <input type="file" accept="image/*" onChange={uploadColorImg} className="hidden" disabled={colorUploading} data-testid="color-upload-input" />
                  </label>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Description</label>
                <textarea rows={3} value={colorForm.color_description} onChange={(e) => setColorForm({ ...colorForm, color_description: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none resize-none" data-testid="color-description" placeholder="Tell members about this month's pour…" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={savingColor} className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-widest uppercase px-5 py-3 hover:bg-orange-600 transition-colors disabled:opacity-50" data-testid="save-color-button">
                  <Save className="w-4 h-4" /> {savingColor ? "Saving…" : "Save Color of the Month"}
                </button>
              </div>
            </form>

            <h2 className="font-display text-2xl uppercase tracking-tight mb-4">Subscribers</h2>
            {subscribers.length === 0 ? (
              <p className="text-slate-500 text-sm" data-testid="subscribers-empty">No subscribers yet.</p>
            ) : (
              <div className="border border-white/5 divide-y divide-white/5" data-testid="admin-subscriber-list">
                {subscribers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-4 px-4 py-4 flex-wrap" data-testid={`subscriber-${s.id}`}>
                    <div>
                      <p className="font-display uppercase text-sm">{s.name || s.email}</p>
                      <p className="font-mono-plex text-[11px] text-slate-500">{s.email}</p>
                      {s.shipping && s.shipping.line1 && (
                        <p className="font-mono-plex text-[11px] text-slate-500 mt-1">
                          {[s.shipping.line1, s.shipping.city, s.shipping.state, s.shipping.postal_code].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-mono-plex text-xs uppercase tracking-widest text-orange-500">{s.tier_label} · ${Number(s.amount).toFixed(2)}/mo</p>
                      <span className={`inline-block mt-1 font-mono-plex text-[10px] tracking-widest uppercase px-2 py-0.5 ${s.status === "active" ? "bg-green-500/15 text-green-400" : s.status === "cancelling" ? "bg-yellow-500/15 text-yellow-400" : "bg-slate-500/15 text-slate-400"}`}>
                        {s.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "library" && (
          <div data-testid="admin-library">
            <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Options Library</h1>
            <p className="text-slate-400 text-sm mb-8 max-w-2xl">Manage every color and finish once. Toggle a swatch <span className="text-orange-400">In Stock</span> and it automatically appears as a customer option — the Custom Pour product shows all in-stock items, and regular baits pick from your in-stock colors.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { g: "color", l: "Drift Colors (finished recipes)" },
                { g: "pigment", l: "Pigment / Base Color (raw)" },
                { g: "mica", l: "Mica Powder" },
                { g: "sheen", l: "Sheen" },
                { g: "pearl", l: "Pearl" },
                { g: "glitter", l: "Glitter" },
              ].map(({ g, l }) => (
                <div key={g} className="border border-white/5 bg-[#0A1118] p-5" data-testid={`library-group-${g}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl uppercase tracking-tight">{l}</h2>
                    <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">
                      {library[g].filter((o) => o.in_stock).length}/{library[g].length} in stock
                    </span>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={newOpt[g]}
                      onChange={(e) => setNewOpt((s) => ({ ...s, [g]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(g); } }}
                      placeholder={`Add a ${l.toLowerCase().replace(/s$/, "")}…`}
                      className="flex-1 bg-transparent border-b border-white/20 py-1.5 text-sm focus:border-orange-500 focus:outline-none"
                      data-testid={`lib-add-input-${g}`}
                    />
                    <button onClick={() => addOption(g)} className="bg-orange-500 text-[#04070A] px-3 hover:bg-orange-600 transition-colors" data-testid={`lib-add-btn-${g}`}><Plus className="w-4 h-4" /></button>
                  </div>
                  <div className="max-h-56 overflow-y-auto flex flex-col divide-y divide-white/5">
                    {library[g].length === 0 ? (
                      <p className="text-slate-600 text-xs py-2">None yet.</p>
                    ) : (
                      library[g].map((o) => (
                        <div key={o.id} className="flex items-center justify-between gap-3 py-2" data-testid={`lib-opt-${o.id}`}>
                          <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                            <input type="checkbox" checked={o.in_stock} onChange={(e) => toggleOption(o.id, e.target.checked)} className="accent-orange-500 w-4 h-4 shrink-0" data-testid={`lib-stock-${o.id}`} />
                            <span className={`text-sm truncate ${o.in_stock ? "text-slate-200" : "text-slate-600 line-through"}`}>{o.name}</span>
                          </label>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <input
                              type="color"
                              value={o.hex || "#888888"}
                              onChange={(e) => setOptionHex(o.id, e.target.value)}
                              title={o.hex ? "Edit swatch color" : "Set swatch color"}
                              className={`w-6 h-6 rounded-full cursor-pointer bg-transparent p-0 ${o.hex ? "border border-white/20" : "border border-dashed border-white/30 opacity-60"}`}
                              data-testid={`lib-hex-${o.id}`}
                            />
                            {o.hex && (
                              <button
                                onClick={() => setOptionHex(o.id, "")}
                                title="Clear swatch"
                                className="text-slate-600 hover:text-orange-400 transition-colors text-xs leading-none"
                                data-testid={`lib-hex-clear-${o.id}`}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <button onClick={() => deleteOption(o.id)} className="text-slate-600 hover:text-red-400 transition-colors shrink-0" data-testid={`lib-del-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))
                    )}
                  </div>
                  <details className="mt-3">
                    <summary className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 cursor-pointer hover:text-orange-400">Bulk paste</summary>
                    <textarea
                      rows={3}
                      value={bulkOpt[g]}
                      onChange={(e) => setBulkOpt((s) => ({ ...s, [g]: e.target.value }))}
                      placeholder="Paste many, separated by commas or new lines"
                      className="w-full bg-transparent border border-white/15 mt-2 p-2 text-xs focus:border-orange-500 focus:outline-none resize-none"
                      data-testid={`lib-bulk-input-${g}`}
                    />
                    <button onClick={() => bulkAddOptions(g)} className="mt-2 border border-white/20 font-mono-plex text-[10px] tracking-widest uppercase px-4 py-2 hover:bg-white/5 transition-colors" data-testid={`lib-bulk-btn-${g}`}>Add All</button>
                  </details>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "recipes" && (
          <div data-testid="recipes-admin-panel">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <p className="text-slate-400 text-sm max-w-2xl">Recipes your anglers built, plus <span className="text-orange-400">Starter Blends</span> you create. Tap <span className="text-orange-400">Save as Color</span> to add a favorite to your Library, or <span className="text-orange-400">Feature</span> a recipe as a one-tap Starter Blend on the Custom Pour page.</p>
              <button
                onClick={() => setShowBlendForm((s) => !s)}
                className="flex items-center gap-2 shrink-0 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-orange-600 transition-colors"
                data-testid="new-blend-toggle"
              >
                <Plus className="w-4 h-4" /> {showBlendForm ? "Close" : "New Starter Blend"}
              </button>
            </div>

            {showBlendForm && (
              <div className="border border-orange-500/30 bg-[#0A1118] p-5 mb-8" data-testid="blend-builder">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-slate-500">Blend Name</label>
                    <input
                      type="text"
                      value={blend.name}
                      onChange={(e) => setBlend((b) => ({ ...b, name: e.target.value }))}
                      placeholder="e.g. Northern Sunset"
                      className="w-full bg-transparent border border-white/15 mt-2 p-2.5 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                      data-testid="blend-name"
                    />
                  </div>
                  <div>
                    <label className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-slate-500">Product Type <span className="text-slate-600">(optional)</span></label>
                    <select
                      value={blend.product_type}
                      onChange={(e) => setBlend((b) => ({ ...b, product_type: e.target.value }))}
                      className="w-full bg-[#0A1118] border border-white/15 mt-2 p-2.5 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                      data-testid="blend-type"
                    >
                      <option value="">Any</option>
                      {Object.keys(blendTypes).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  onClick={() => setBlend((b) => ({ ...b, split: !b.split }))}
                  className={`mt-4 flex items-center gap-2 font-mono-plex text-[11px] tracking-wide px-3 py-2 border transition-colors ${
                    blend.split ? "bg-orange-500/15 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                  }`}
                  data-testid="blend-split-toggle"
                >
                  <span className={`w-8 h-4 rounded-full relative shrink-0 transition-colors ${blend.split ? "bg-orange-500" : "bg-white/15"}`}>
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${blend.split ? "left-4" : "left-0.5"}`} />
                  </span>
                  Split-color bait (top &amp; bottom)
                </button>

                {!blend.split ? (
                  <div className="mt-5">
                    <AdminLayerPicker library={library} layer={blendSingle} onToggle={toggleBlendItem(setBlendSingle)} idPrefix="blend-single" />
                  </div>
                ) : (
                  <div className="mt-5 grid md:grid-cols-2 gap-5">
                    <div className="border border-white/10 p-3">
                      <span className="font-mono-plex text-[11px] tracking-[0.15em] uppercase text-orange-400">Top</span>
                      <div className="mt-3"><AdminLayerPicker library={library} layer={blendTop} onToggle={toggleBlendItem(setBlendTop)} idPrefix="blend-top" /></div>
                    </div>
                    <div className="border border-white/10 p-3">
                      <span className="font-mono-plex text-[11px] tracking-[0.15em] uppercase text-orange-400">Bottom</span>
                      <div className="mt-3"><AdminLayerPicker library={library} layer={blendBottom} onToggle={toggleBlendItem(setBlendBottom)} idPrefix="blend-bottom" /></div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 mt-5">
                  <button
                    onClick={createStarterBlend}
                    disabled={savingBlend}
                    className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-orange-600 transition-colors disabled:opacity-50"
                    data-testid="blend-save"
                  >
                    <Save className="w-3.5 h-3.5" /> {savingBlend ? "Saving…" : "Create Starter Blend"}
                  </button>
                  <button onClick={resetBlendForm} className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500 hover:text-slate-300" data-testid="blend-cancel">Cancel</button>
                </div>
              </div>
            )}

            {recipes.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center">
                <p className="text-slate-500 text-sm">No recipes or Starter Blends yet — tap <span className="text-orange-400">New Starter Blend</span> to create your first.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {recipes.map((r) => (
                  <div key={r.id} className="border border-white/10 bg-[#0A1118] p-5" data-testid={`admin-recipe-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-display text-xl uppercase tracking-tight leading-none">{r.name || "Untitled"}</h3>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {r.featured && <span className="font-mono-plex text-[9px] tracking-widest uppercase text-orange-400 border border-orange-400/40 px-2 py-1">Starter</span>}
                        {r.promoted && <span className="font-mono-plex text-[9px] tracking-widest uppercase text-[#AEB358] border border-[#AEB358]/40 px-2 py-1">In Library</span>}
                      </div>
                    </div>
                    <p className="font-mono-plex text-[10px] text-slate-500 mt-1">{r.source === "admin" ? "Created by you" : r.customer_email}</p>
                    <dl className="mt-3 space-y-1 font-mono-plex text-xs text-slate-400">
                      {r.product_type && <div><span className="text-slate-600">Type: </span>{r.product_type}</div>}
                      {r.split ? (
                        <>
                          <div><span className="text-slate-600">Top: </span>{adminLayerLine(r.top) || "—"}</div>
                          <div><span className="text-slate-600">Bottom: </span>{adminLayerLine(r.bottom) || "—"}</div>
                        </>
                      ) : adminLayerHas(r.base) ? (
                        <div>{adminLayerLine(r.base)}</div>
                      ) : (
                        <>
                          {r.base_pigment && <div><span className="text-slate-600">Base: </span>{r.base_pigment}</div>}
                          {r.mica && <div><span className="text-slate-600">Mica: </span>{r.mica}</div>}
                          {r.pearl && <div><span className="text-slate-600">Pearl: </span>{r.pearl}</div>}
                          {r.glitter && <div><span className="text-slate-600">Glitter: </span>{r.glitter}</div>}
                        </>
                      )}
                      {r.instructions && <div className="text-slate-500 italic">“{r.instructions}”</div>}
                    </dl>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => promoteRecipe(r.id)}
                        disabled={r.promoted}
                        className="flex items-center justify-center gap-2 border font-mono-plex text-[10px] tracking-[0.15em] uppercase py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-[#AEB358]/60 text-[#AEB358] hover:bg-[#AEB358]/10"
                        data-testid={`promote-recipe-${r.id}`}
                      >
                        <Palette className="w-3.5 h-3.5" /> {r.promoted ? "In Colors" : "Save as Color"}
                      </button>
                      <button
                        onClick={() => toggleFeatureRecipe(r.id)}
                        className={`flex items-center justify-center gap-2 border font-mono-plex text-[10px] tracking-[0.15em] uppercase py-2.5 transition-colors ${
                          r.featured ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-orange-500/60 text-orange-400 hover:bg-orange-500/10"
                        }`}
                        data-testid={`feature-recipe-${r.id}`}
                      >
                        <Star className="w-3.5 h-3.5" /> {r.featured ? "Featured" : "Feature"}
                      </button>
                    </div>
                    <button
                      onClick={() => deleteRecipe(r.id)}
                      className="mt-2 w-full flex items-center justify-center gap-2 border border-white/10 font-mono-plex text-[10px] tracking-[0.15em] uppercase py-2 text-slate-500 hover:text-red-400 hover:border-red-400/40 transition-colors"
                      data-testid={`delete-recipe-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "ambassadors" && (
          <div data-testid="ambassadors-panel">
            {/* Settings */}
            <div className="border border-white/10 bg-[#0A1118] p-5 mb-8 flex flex-wrap items-end gap-5">
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Discount %</label>
                <input type="number" min="0" max="100" value={ambSettings.ambassador_discount_pct} onChange={(e) => setAmbSettings({ ...ambSettings, ambassador_discount_pct: e.target.value })} className="w-24 block bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none mt-1" data-testid="amb-setting-discount" />
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Points per Free Pack</label>
                <input type="number" min="1" value={ambSettings.ambassador_points_per_pack} onChange={(e) => setAmbSettings({ ...ambSettings, ambassador_points_per_pack: e.target.value })} className="w-28 block bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none mt-1" data-testid="amb-setting-threshold" />
              </div>
              <button onClick={saveAmbSettings} className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-widest uppercase px-5 py-2.5 hover:bg-orange-600 transition-colors" data-testid="amb-settings-save"><Save className="w-3.5 h-3.5" /> Save</button>
            </div>

            {/* Monthly winner */}
            <div className="border border-[#AEB358]/30 bg-[#AEB358]/5 p-5 mb-8" data-testid="monthly-winner-card">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#AEB358]" />
                <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-400">Monthly Winner · {monthly.month}</span>
              </div>
              {monthly.rows?.length ? (
                <>
                  <div className="mt-3 space-y-1.5">
                    {monthly.rows.slice(0, 3).map((r, i) => (
                      <div key={r.id} className="flex items-center justify-between" data-testid={`monthly-row-${i + 1}`}>
                        <span className={`text-sm ${i === 0 ? "text-[#AEB358] font-semibold" : "text-slate-300"}`}>{i === 0 ? "🏆 " : `#${i + 1} `}{r.name} <span className="text-slate-500">({r.code})</span></span>
                        <span className="font-mono-plex text-xs text-slate-400">{r.month_points} this month</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-white/10">
                    <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500 w-full">Reward the leader ({monthly.rows[0].name})</span>
                    <div>
                      <label className="font-mono-plex text-[9px] tracking-widest uppercase text-slate-500">Bonus pts</label>
                      <input type="number" min="1" value={bonusPts} onChange={(e) => setBonusPts(e.target.value)} className="w-20 block bg-transparent border-b border-white/20 py-1.5 focus:border-orange-500 focus:outline-none" data-testid="bonus-points-input" />
                    </div>
                    <button onClick={() => awardWinner(monthly.rows[0].id, "points")} className="border border-white/20 font-mono-plex text-[10px] tracking-widest uppercase px-4 py-2 hover:bg-white/5 transition-colors" data-testid="award-points-btn">Award Points</button>
                    <button onClick={() => awardWinner(monthly.rows[0].id, "pack")} className="bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-widest uppercase px-4 py-2 hover:bg-orange-600 transition-colors" data-testid="award-pack-btn">Award Free Pack</button>
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-sm mt-2">No paid referrals yet this month.</p>
              )}
            </div>


            {/* Create ambassador */}
            <form onSubmit={createAmbassador} className="border border-white/10 bg-[#0A1118] p-5 mb-8 flex flex-wrap items-end gap-4" data-testid="ambassador-create-form">
              <div className="flex-1 min-w-[160px]">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Name</label>
                <input required value={newAmb.name} onChange={(e) => setNewAmb({ ...newAmb, name: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none mt-1" data-testid="amb-new-name" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Email</label>
                <input required type="email" value={newAmb.email} onChange={(e) => setNewAmb({ ...newAmb, email: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none mt-1" data-testid="amb-new-email" />
              </div>
              <button type="submit" className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-widest uppercase px-5 py-2.5 hover:bg-orange-600 transition-colors" data-testid="amb-create-btn"><Plus className="w-3.5 h-3.5" /> Add Ambassador</button>
            </form>
            <p className="text-slate-500 text-xs mb-6">The ambassador logs in with a customer account using this same email to see their dashboard. A unique code is generated automatically.</p>

            {/* Ambassador list */}
            {ambassadors.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center"><p className="text-slate-500 text-sm">No ambassadors yet.</p></div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ambassadors.map((a) => (
                  <div key={a.id} className={`border bg-[#0A1118] p-5 ${a.active ? "border-white/10" : "border-white/5 opacity-60"}`} data-testid={`ambassador-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-display text-xl uppercase tracking-tight leading-none">{a.name}</h3>
                        <p className="font-mono-plex text-[10px] text-slate-500 mt-1">{a.email}</p>
                      </div>
                      <button onClick={() => deleteAmbassador(a.id)} className="text-slate-600 hover:text-red-400 transition-colors shrink-0" data-testid={`amb-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <p className="font-display text-2xl uppercase tracking-tight text-orange-500 mt-3" data-testid={`amb-code-${a.id}`}>{a.code}</p>
                    <div className="flex gap-4 mt-3 font-mono-plex text-xs text-slate-400">
                      <span><span className="text-white text-sm">{a.points_available}</span> pts</span>
                      <span><span className="text-white text-sm">{a.referrals}</span> referrals</span>
                      <span><span className="text-white text-sm">{a.packs_available}</span> free packs</span>
                    </div>
                    <label className="flex items-center gap-2 mt-4 cursor-pointer">
                      <input type="checkbox" checked={a.active} onChange={(e) => toggleAmbassador(a.id, e.target.checked)} className="accent-orange-500 w-4 h-4" data-testid={`amb-active-${a.id}`} />
                      <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-400">Active</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Redemptions */}
            <h3 className="font-display text-2xl uppercase tracking-tight mt-12 mb-4">Redemption Requests</h3>
            {redemptions.length === 0 ? (
              <p className="text-slate-500 text-sm">No redemption requests yet.</p>
            ) : (
              <div className="space-y-3">
                {redemptions.map((r) => (
                  <div key={r.id} className="flex items-center justify-between flex-wrap gap-3 border border-white/10 bg-[#0A1118] px-5 py-4" data-testid={`redemption-${r.id}`}>
                    <div>
                      <span className="text-slate-200 text-sm">{r.ambassador_name} <span className="text-slate-500">({r.ambassador_code})</span></span>
                      <p className="font-mono-plex text-[10px] text-slate-500 mt-0.5">{r.packs} free pack · {r.points_spent} pts · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    {r.status === "fulfilled" ? (
                      <span className="font-mono-plex text-[10px] tracking-widest uppercase px-3 py-1.5 border border-[#AEB358]/40 text-[#AEB358]">Fulfilled</span>
                    ) : (
                      <button onClick={() => fulfillRedemption(r.id)} className="flex items-center gap-2 bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-widest uppercase px-4 py-2 hover:bg-orange-600 transition-colors" data-testid={`fulfill-${r.id}`}><Check className="w-3.5 h-3.5" /> Mark Fulfilled</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0A1118] border border-white/10 w-full max-w-2xl my-8" data-testid="product-form">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
              <h2 className="font-display text-2xl uppercase tracking-tight">{editing ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setShowForm(false)} data-testid="close-form"><X className="w-5 h-5 text-slate-400 hover:text-orange-500" /></button>
            </div>
            <form onSubmit={save} className="p-6 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Name</label>
                <input
                  type="text" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none"
                  data-testid="form-name"
                />
              </div>

              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Category</label>
                {showNewCat ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text" required autoFocus
                      placeholder="New category name"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none"
                      data-testid="form-category-new"
                    />
                    <button type="button" onClick={() => { setShowNewCat(false); setForm({ ...form, category: "" }); }} className="text-slate-500 hover:text-orange-400 shrink-0" data-testid="form-category-cancel-new"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <select
                    required
                    value={form.category}
                    onChange={(e) => {
                      if (e.target.value === "__new__") { setShowNewCat(true); setForm({ ...form, category: "" }); }
                      else setForm({ ...form, category: e.target.value });
                    }}
                    className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none text-slate-200 [&>option]:bg-[#0A1118]"
                    data-testid="form-category"
                  >
                    <option value="">Select a category…</option>
                    {[...new Set(products.map((p) => p.category).filter((c) => c && c.trim() && c.trim().toLowerCase() !== "all"))].sort().map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__new__">+ Add new category…</option>
                  </select>
                )}
              </div>

              {[
                { k: "price", l: "Price (USD)", req: true, type: "number" },
                { k: "stock", l: "Stock (qty)", type: "number" },
                { k: "length", l: "Length (e.g. 4.5\")" },
                { k: "pack_size", l: "Pack Size (e.g. 6 pack)" },
              ].filter((f) => !(form.custom && (f.k === "length" || f.k === "pack_size"))).map((f) => (
                <div key={f.k}>
                  <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">{f.l}</label>
                  <input
                    type={f.type || "text"} step="any" required={f.req}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none"
                    data-testid={`form-${f.k}`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none resize-none" data-testid="form-description" />
              </div>
              {!form.custom && (
                <div className="sm:col-span-2" data-testid="colors-picker">
                  <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Colors — pick from your in-stock Library</label>
                  {library.color.filter((o) => o.in_stock).length === 0 ? (
                    <p className="text-slate-500 text-xs mt-2">No in-stock colors yet. Add them in the <button type="button" onClick={() => { setShowForm(false); setTab("library"); }} className="text-orange-400 underline">Library</button> tab.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {library.color.filter((o) => o.in_stock).map((o) => {
                        const selected = form.colors.split(",").map((s) => s.trim()).filter(Boolean).includes(o.name);
                        return (
                          <button
                            type="button"
                            key={o.id}
                            onClick={() => toggleFormColor(o.name)}
                            className={`font-mono-plex text-xs tracking-wide px-3 py-1.5 border transition-colors ${selected ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400 hover:border-white/40"}`}
                            data-testid={`form-color-${o.name}`}
                          >
                            {o.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Product Images</label>
                <div className="flex flex-wrap gap-3 mt-3">
                  {form.images.split(",").map((s) => s.trim()).filter(Boolean).map((url) => (
                    <div key={url} className="relative w-20 h-20 bg-[#04070A] border border-white/10 overflow-hidden group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        className="absolute top-0 right-0 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="remove-image"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-orange-500 transition-colors" data-testid="upload-image-label">
                    <UploadCloud className="w-5 h-5 text-slate-400" />
                    <span className="font-mono-plex text-[9px] uppercase text-slate-500">{uploading ? "…" : "Upload"}</span>
                    <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="upload-image-input" disabled={uploading} />
                  </label>
                </div>
                <textarea rows={2} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 mt-3 focus:border-orange-500 focus:outline-none resize-none text-xs text-slate-500" data-testid="form-images" placeholder="…or paste image URLs, comma separated" />
              </div>
              <div className="flex items-center gap-6 sm:col-span-2">
                <label className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-orange-500 w-4 h-4" data-testid="form-featured" /> Featured
                </label>
                <label className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest cursor-pointer">
                  <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} className="accent-orange-500 w-4 h-4" data-testid="form-instock" /> In Stock
                </label>
                <label className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest cursor-pointer">
                  <input type="checkbox" checked={form.custom} onChange={(e) => setForm({ ...form, custom: e.target.checked })} className="accent-orange-500 w-4 h-4" data-testid="form-custom" /> Custom Request
                </label>
              </div>
              {form.custom && (
                <div className="sm:col-span-2 border border-orange-500/20 bg-orange-500/[0.03] p-4 space-y-2" data-testid="custom-fields">
                  <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-orange-400">Custom Pour Product</p>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    Customers pick a <strong className="text-slate-200">Product Type</strong> (Swimbait · Lizard · Worm) — length and pack size are set automatically. Then they either <strong className="text-slate-200">pick a finished Drift Color</strong> or <strong className="text-slate-200">build their own recipe</strong> (Base Pigment + Mica/Pearl/Glitter), pulled live from your <button type="button" onClick={() => { setShowForm(false); setTab("library"); }} className="text-orange-400 underline">in-stock Library</button>, plus a Special Instructions box. No need to set colors or length here.
                  </p>
                </div>
              )}
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 border border-white/20 font-mono-plex text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-3 hover:bg-orange-600 transition-colors disabled:opacity-50" data-testid="save-product-button">
                  {saving ? "Saving…" : editing ? "Update Product" : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
